import { useState } from "react";
import { toast } from "sonner";
import {
  MapPin, Clock, Church, ChevronDown, Flame, ArrowRight, Sparkles,
  Send, User, Mail, Phone, Calendar, MessageSquare
} from "lucide-react";
import { CITIES_MINISTRY } from "@/lib/site-data";
import { api } from "@/lib/api";

const HERO = "/assets/ministry/belver-pulpit.jpg";
const BELVER_IMG = "/assets/ministry/belver-pulpit.jpg";
const LAETITIA_IMG = "/assets/ministry/laetitia-preaching.png";

export default function Ministry() {
  // Temporarily hidden — set to true to show villes visitées again
  const SHOW_CITIES = false;

  const [expanded, setExpanded] = useState(CITIES_MINISTRY[0]?.id);

  const [form, setForm] = useState({
    name: "", church_city: "", email: "", phone: "", desired_date: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact/invitation", form);
      toast.success(data.message || "Invitation envoyée");
      setForm({ name: "", church_city: "", email: "", phone: "", desired_date: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-ministry" className="bg-[#F7F5F0]">
      {/* Hero */}
      <section className="relative min-h-[80vh] overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Prophète Belver au pupitre" className="w-full h-full object-cover jt-pan" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#14100B]/95 via-[#2E4959]/80 to-[#4A6D82]/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#F7F5F0] via-transparent to-transparent" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 pt-40 pb-24 md:pt-48 md:pb-32">
          <span className="text-xs uppercase tracking-[0.3em] text-[#F4E4C1] flex items-center gap-2 mb-6">
            <Flame className="w-3.5 h-3.5" /> Ministère prophétique · Réveil · Délivrance
          </span>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[1.02] text-white mb-6 max-w-4xl">
            L'appel du réveil,<br /><em className="not-italic text-[#F4E4C1]">d'église en église</em>.
          </h1>
          <p className="text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
            Le ministère prophétique de <strong className="text-[#F4E4C1]">Prophète Belver</strong> — un appel à la délivrance et au réveil pour soutenir et fortifier les églises — accompagné de son épouse, la servante <strong className="text-[#F4E4C1]">Laetitia</strong>, dans la prière et la modération.
          </p>
        </div>
      </section>

      {/* Prophète Belver */}
      <section className="py-20 md:py-28 bg-white" data-testid="section-belver">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden jt-medium-shadow">
              <img src={BELVER_IMG} alt="Prophète Belver prêchant" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#4A6D82]/35 to-[#7B98A8]/30 mix-blend-multiply pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2E4959]/50 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 text-white">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#F4E4C1]">Le mandat prophétique</p>
                <p className="font-serif italic text-2xl mt-1">Prophète Belver</p>
              </div>
            </div>
          </div>
          <div className="md:col-span-7">
            <span className="text-xs uppercase tracking-[0.3em] text-[#C99A5B]">Prophète · Réveil · Délivrance</span>
            <h2 className="font-serif text-4xl md:text-6xl text-[#1E2933] mt-3 mb-6 leading-[1.05]">
              Un ministère de <em className="not-italic text-[#4A6D82]">réveil</em> et de <em className="not-italic text-[#4A6D82]">délivrance</em>.
            </h2>
            <p className="text-[#1E2933]/85 text-lg leading-relaxed mb-5">
              Au-delà de notre plateforme de contenu, nous répondons à l'appel de Dieu pour le corps de Christ.
            </p>
            <p className="text-[#1E2933]/85 text-lg leading-relaxed mb-5">
              Le mandat de <strong>Prophète Belver</strong> est clair : se déplacer d'église en église pour porter un ministère prophétique de <strong>réveil</strong> et de <strong>délivrance</strong>. Sa mission est d'encourager, de soutenir et de relever les assemblées locales, et surtout, de <em>réveiller les âmes qui ont besoin d'un nouveau feu de l'Esprit</em>.
            </p>
            <blockquote className="font-serif italic text-lg md:text-xl text-[#4A6D82] border-l-2 border-[#C99A5B] pl-5 my-2">
              « L'Esprit du Seigneur, l'Éternel, est sur moi, car l'Éternel m'a oint pour porter de bonnes nouvelles aux malheureux ; il m'a envoyé pour guérir ceux qui ont le cœur brisé, pour proclamer aux captifs la liberté, et aux prisonniers la délivrance. »
              <span className="block text-sm not-italic text-[#6B7A85] mt-2">— Ésaïe 61:1</span>
            </blockquote>
          </div>
        </div>
      </section>

      {/* Laetitia */}
      <section className="py-20 md:py-28 bg-[#F7F5F0]" data-testid="section-laetitia-ministry">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 order-2 md:order-1">
            <span className="text-xs uppercase tracking-[0.3em] text-[#C99A5B]">Prière · Modération · Sentinelle</span>
            <h2 className="font-serif text-4xl md:text-6xl text-[#1E2933] mt-3 mb-6 leading-[1.05]">
              Aux côtés du prophète, <em className="not-italic text-[#4A6D82]">la servante Laetitia</em>.
            </h2>
            <p className="text-[#1E2933]/85 text-lg leading-relaxed mb-5">
              En tant qu'épouse, elle accompagne le Prophète Belver dans ce ministère en assurant la <strong>modération</strong> et l'<strong>atmosphère de prière</strong>. Son rôle est d'apporter ce soutien spirituel indispensable pour accueillir et guider les âmes touchées lors de ces missions.
            </p>
            <p className="text-[#1E2933]/85 text-lg leading-relaxed">
              C'est un travail de <strong>sentinelles</strong> : nous allons là où l'Esprit nous conduit, pour proclamer les oracles de Dieu et fortifier le Corps de Christ.
            </p>
          </div>
          <div className="md:col-span-5 order-1 md:order-2">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden jt-medium-shadow">
              <img src={LAETITIA_IMG} alt="Servante Laetitia en intervention" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#4A6D82]/35 to-[#7B98A8]/30 mix-blend-multiply pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2E4959]/50 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 text-white">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#F4E4C1]">L'atmosphère de prière</p>
                <p className="font-serif italic text-2xl mt-1">Servante Laetitia</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cities */}
      {SHOW_CITIES && (
      <section className="py-20 md:py-24 bg-white" data-testid="section-cities">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-12">
            <span className="text-xs uppercase tracking-[0.3em] text-[#4A6D82]">Ils nous ont accueillis</span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1E2933] mt-3">Villes visitées</h2>
            <p className="text-[#6B7A85] mt-3">Clique sur une ville pour voir les églises et les horaires de culte.</p>
          </div>

          <div className="space-y-4">
            {CITIES_MINISTRY.map((c) => {
              const isOpen = expanded === c.id;
              return (
                <div key={c.id} data-testid={`city-${c.id}`}
                  className={`rounded-2xl border transition-all ${isOpen ? "border-[#4A6D82] bg-[#E4EBEF]/40" : "border-[#DDE3E8] bg-white hover:border-[#7B98A8]"}`}>
                  <button onClick={() => setExpanded(isOpen ? null : c.id)} data-testid={`city-toggle-${c.id}`}
                    className="w-full flex items-center justify-between p-5 md:p-6">
                    <div className="flex items-center gap-4">
                      <div className={`h-11 w-11 rounded-full flex items-center justify-center ${isOpen ? "bg-[#4A6D82] text-white" : "bg-[#E4EBEF] text-[#4A6D82]"}`}>
                        <MapPin className="w-5 h-5" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-serif text-2xl text-[#1E2933] leading-tight">{c.city}</h3>
                        <p className="text-xs text-[#6B7A85] uppercase tracking-widest">{c.region} · {c.country}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-[#6B7A85] hidden md:inline">
                        {c.churches.length} église{c.churches.length !== 1 ? "s" : ""}
                      </span>
                      <ChevronDown className={`w-5 h-5 text-[#4A6D82] transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 md:px-6 md:pb-6 grid gap-3 jt-fadeUp">
                      {c.churches.map((ch, i) => (
                        <div key={i} className="p-5 rounded-xl bg-white border border-[#DDE3E8] flex gap-4">
                          <div className="h-10 w-10 rounded-full bg-[#C99A5B]/20 text-[#C99A5B] flex items-center justify-center shrink-0">
                            <Church className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-serif text-xl text-[#1E2933] leading-tight">{ch.name}</h4>
                            <p className="text-sm text-[#6B7A85] mt-1 flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5" /> {ch.address}
                            </p>
                            <p className="text-sm text-[#4A6D82] mt-1 flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5" /> {ch.service}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-sm text-[#6B7A85] mt-10 italic text-center">
            D'autres villes bientôt. La liste sera enrichie au fil des déplacements.
          </p>
        </div>
      </section>
      )}

      {/* Invitation form */}
      <section className="relative py-20 md:py-28 bg-[#2E4959] text-white overflow-hidden" data-testid="section-invitation">
        <div className="absolute inset-0 opacity-15 jt-grain" />
        <div className="absolute -left-32 top-20 w-96 h-96 rounded-full bg-[#C99A5B]/20 blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-xs uppercase tracking-[0.3em] text-[#F4E4C1] flex items-center justify-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Pasteurs & responsables
            </span>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] mt-4 mb-4">
              Invite <em className="not-italic text-[#F4E4C1]">le souffle du réveil</em>.
            </h2>
            <p className="text-white/85 text-lg max-w-2xl mx-auto">
              Vous souhaitez accueillir Prophète Belver et la servante Laetitia dans votre église pour prêcher, prier et fortifier votre assemblée ? Remplissez le formulaire ci-dessous, nous répondrons personnellement.
            </p>
          </div>

          <form onSubmit={submit} data-testid="invitation-form" className="bg-white rounded-3xl p-6 md:p-10 grid gap-5 text-[#1E2933]">
            <div className="grid md:grid-cols-2 gap-5">
              <Field icon={<User className="w-4 h-4" />} label="Votre nom" required>
                <input data-testid="contact-name" required value={form.name} onChange={setF("name")} placeholder="Jean Dupont" className="w-full bg-transparent focus:outline-none" />
              </Field>
              <Field icon={<Church className="w-4 h-4" />} label="Église / Ville" required>
                <input data-testid="contact-church" required value={form.church_city} onChange={setF("church_city")} placeholder="Église Bethel, Paris" className="w-full bg-transparent focus:outline-none" />
              </Field>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <Field icon={<Mail className="w-4 h-4" />} label="Email" required>
                <input data-testid="contact-email" required type="email" value={form.email} onChange={setF("email")} placeholder="pasteur@eglise.fr" className="w-full bg-transparent focus:outline-none" />
              </Field>
              <Field icon={<Phone className="w-4 h-4" />} label="Téléphone (optionnel)">
                <input data-testid="contact-phone" value={form.phone} onChange={setF("phone")} placeholder="+33 6 12 34 56 78" className="w-full bg-transparent focus:outline-none" />
              </Field>
            </div>
            <Field icon={<Calendar className="w-4 h-4" />} label="Date souhaitée">
              <input data-testid="contact-date" type="date" value={form.desired_date} onChange={setF("desired_date")} className="w-full bg-transparent focus:outline-none" />
            </Field>
            <Field icon={<MessageSquare className="w-4 h-4" />} label="Votre message" required multiline>
              <textarea data-testid="contact-message" required value={form.message} onChange={setF("message")} rows={5}
                placeholder="Décrivez brièvement l'événement, le contexte de l'invitation et vos attentes."
                className="w-full bg-transparent focus:outline-none resize-none" />
            </Field>
            <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
              <p className="text-xs text-[#6B7A85] max-w-md flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5" /> Nous intervenons en France, Belgique, Suisse, Afrique francophone et au-delà.
              </p>
              <button disabled={loading} type="submit" data-testid="contact-submit"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#4A6D82] text-white hover:bg-[#2E4959] transition disabled:opacity-60 jt-blue-glow">
                <Send className="w-4 h-4" />
                {loading ? "Envoi…" : "Envoyer l'invitation"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ icon, label, required, multiline, children }) {
  return (
    <label className="block rounded-2xl border border-[#DDE3E8] bg-[#F7F5F0] px-4 py-3 focus-within:border-[#4A6D82] focus-within:ring-2 focus-within:ring-[#4A6D82]/30 transition">
      <div className="flex items-center gap-2 text-xs text-[#6B7A85] mb-1.5">
        <span className="text-[#4A6D82]">{icon}</span>
        <span className="uppercase tracking-widest">{label}{required && <span className="text-[#C99A5B]"> *</span>}</span>
      </div>
      {children}
    </label>
  );
}
